import React, {useState, useEffect} from 'react';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Container from '@material-ui/core/Container';
import LogoDarkTheme from './../components/img/setupad-logo-UA-white.svg';
import LogoWhiteTheme from './../components/img/setupad-logo-UA-darkblue.svg';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Link from '@material-ui/core/Link';
import LinearProgress from '@material-ui/core/LinearProgress';
import {makeStyles} from '@material-ui/core/styles/index';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import {APICall} from '../api/api';
import usePersistentAuthStore from '../store/persistentAuthStore';
import usePersistentStore from '../store/persistentStore';

const useStyles = makeStyles(theme => ({
  loader: {
    position: 'absolute',
    zIndex: 99999,
    top: '0',
    width: '100%',
    left: 0,
  },
  additionalButtons: {
    margin: theme.spacing(1, 0, 1.5),
  },
  formFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 2,
  },
  keepSignedIn: {
    margin: 0,
    marginLeft: -9,
    '& .MuiFormControlLabel-label': {
      fontSize: 13,
      letterSpacing: 0.1,
      color: theme.palette.text.secondary,
    },
  },
  keepSignedInCheckbox: {
    padding: 6,
    '& svg': {
      fontSize: 18,
    },
  },
  footerLink: {
    fontSize: 13,
  },
  pageBody: {
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  signupRow: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 24,
    textAlign: 'center',
  },
}));

const getPostLoginPath = location => {
  const from = location.state && location.state.from;
  const fromPath = from && (typeof from === 'string' ? from : from.pathname + (from.search || ''));
  const next = new URLSearchParams(location.search).get('next');
  const candidate = fromPath || next || '/home';
  const isSafe =
    candidate.startsWith('/') &&
    !candidate.startsWith('//') &&
    !candidate.startsWith('/login') &&
    !candidate.startsWith('/wb/login');
  return isSafe ? candidate : '/home';
};

function Login(props) {
  const classes = useStyles();
  const [credentials, setCredentials] = useState({
    _username: '',
    _password: '',
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [credentialsChanged, setCredentialsChanged] = useState(null);
  const {auth, setAuth, clearAuth} = usePersistentAuthStore();
  const keepSignedIn = usePersistentStore(state => state.keepSignedIn) !== false;
  const setPersistentState = usePersistentStore(state => state.setState);

  useEffect(() => {
    if (auth && auth.token) {
      props.history.push(getPostLoginPath(props.location));
    }
  }, [auth]);

  let onSubmit = async event => {
    event.preventDefault();
    setLoading(true);

    try {
      let response = await APICall({
        endpoint: 'authenticate',
        options: {
          method: 'post',
          body: JSON.stringify({
            username: credentials._username,
            password: credentials._password,
          }),
          headers: {
            'Cache-Control': 'max-age=0, no-cache',
            'Content-Type': 'application/json',
          },
        },
      });
      if (response.token) {
        setPersistentState('keepSignedIn', keepSignedIn);
        setAuth({
          ...response,
          ...(keepSignedIn ? {} : {refreshToken: undefined}),
        });
      } else {
        setError(response.error);
      }
      setLoading(false);
    } catch (error) {
      setError(`Error. Your IP might be blocked for too many requests, try again in an hour.`);
      setLoading(false);
    }
  };

  let handleInputChange = event => {
    setError(null);
    const {value, name} = event.target;
    credentials[name] = value;
    setCredentials(credentials);
    setCredentialsChanged(true);
  };

  return (
    <>
      {loading && <LinearProgress variant="query" className={classes.loader} position={'fixed'} />}

      <div className={classes.pageBody}>
        <Container component="main" maxWidth="xs" style={{padding: 50}}>
          <img
            src={props.theme === 'dark' ? LogoDarkTheme : LogoWhiteTheme}
            style={{width: '100%'}}
          />
          <form action="/" method="POST" noValidate onSubmit={onSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              fullWidth
              id="username"
              label="Username"
              name="_username"
              autoComplete="user"
              value={credentials.username}
              onChange={handleInputChange}
              autoFocus
              InputLabelProps={{shrink: true}}
            />
            <TextField
              style={{marginTop: '8px'}}
              variant="outlined"
              margin="normal"
              fullWidth
              name="_password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={credentials.password}
              onChange={handleInputChange}
              InputLabelProps={{shrink: true}}
            />
            <Button
              className={classes.additionalButtons}
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading || !credentialsChanged}
            >
              Sign In
            </Button>

            <div className={classes.formFooter}>
              <FormControlLabel
                className={classes.keepSignedIn}
                control={
                  <Checkbox
                    className={classes.keepSignedInCheckbox}
                    checked={keepSignedIn}
                    onChange={event => setPersistentState('keepSignedIn', event.target.checked)}
                    color="primary"
                    name="keepSignedIn"
                  />
                }
                label="Keep me signed in"
              />
              <Link href="/forgot-password" className={classes.footerLink}>
                Forgot password?
              </Link>
            </div>

            {error && (
              <Box mt={2}>
                <Typography variant="body2" color="error" align="center">
                  {error}
                </Typography>
              </Box>
            )}
          </form>
        </Container>
      </div>

      <div className={classes.signupRow}>
        <Link href="https://setupad.com/sign-up/" target={'_blank'} className={classes.footerLink}>
          Don't have an account?
        </Link>
      </div>
    </>
  );
}

export default Login;
